import React, { useState, useEffect } from "react";
import { Pencil } from "lucide-react";
import { getBusinessPartners, updateUser, getCurrentUser } from "../services/api";
import toast from "react-hot-toast";
import UserAvatar from "../components/UserAvatar";

const BACKEND = "https://api.giibportal.cloud";

// Build a safe absolute URL for images
const toAbsoluteUrl = (path) => {
  if (!path) return "";
  const s = String(path).trim();
  if (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("blob:") ||
    s.startsWith("data:")
  ) {
    return s;
  }
  if (s.startsWith("/")) return `${BACKEND}${s}`;
  return `${BACKEND}/${s.replace(/^\/+/, "")}`;
};

// Local fallback avatar (SVG data URI)
const FALLBACK =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="144" height="144" viewBox="0 0 144 144">
      <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#e5e7eb"/><stop offset="1" stop-color="#f3f4f6"/></linearGradient></defs>
      <rect width="144" height="144" rx="72" fill="url(#g)"/>
      <circle cx="72" cy="54" r="26" fill="#d1d5db"/>
      <path d="M20 128a52 52 0 0 1 104 0" fill="#d1d5db"/>
    </svg>`
  );

// === Image helpers ===
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const REJECTED_TYPES = ["image/heic", "image/heif", "image/avif"]; // browsers may not decode these reliably
const MAX_SIDE = 2000; // px
const MAX_BYTES_BEFORE_COMPRESS = 7 * 1024 * 1024; // 7MB

async function downscaleAndCompress(file) {
  // If type is not directly accepted, bail out early
  if (REJECTED_TYPES.includes(file.type)) {
    throw new Error(
      "This image format isn't supported by the browser/backend. Please use JPG/PNG/WebP."
    );
  }

  // If it's already small and an accepted type, return as-is
  if (ACCEPTED_TYPES.includes(file.type) && file.size <= MAX_BYTES_BEFORE_COMPRESS) {
    return file;
  }

  // Try to decode the image
  const blobURL = URL.createObjectURL(file);
  let bitmap;
  try {
    bitmap = await createImageBitmap(await (await fetch(blobURL)).blob());
  } catch (e) {
    URL.revokeObjectURL(blobURL);
    throw new Error("Could not read this image. Try a different file (JPG/PNG/WebP).");
  }
  URL.revokeObjectURL(blobURL);

  // Compute target size
  let { width, height } = bitmap;
  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  const targetW = Math.round(width * scale);
  const targetH = Math.round(height * scale);

  // Draw to canvas
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d", { alpha: true });

  // If original is PNG with transparency and we later export JPEG, set white bg
  if (file.type === "image/png") {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, targetW, targetH);
  }

  ctx.drawImage(bitmap, 0, 0, targetW, targetH);

  // Choose output format: prefer JPEG, fallback to WebP if browser prefers it
  const preferWebP = file.type === "image/webp";
  const outType = preferWebP ? "image/webp" : "image/jpeg";
  const quality = 0.9;

  const outBlob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), outType, quality)
  );

  if (!outBlob) throw new Error("Failed to process the image.");

  // Name the file sensibly
  const ext = outType === "image/webp" ? "webp" : "jpg";
  const outFile = new File([outBlob], `profile.${ext}`, { type: outType, lastModified: Date.now() });
  return outFile;
}

function EditProfile() {
  const [formData, setFormData] = useState({
    id: "",
    firstName: "",
    lastName: "",
    name: "",
    email: "",
    contact: "",
    company: "",
    password: "",
    confirmPassword: "",
    CardCode: "",
    CardName: "",
    street: "",
    city: "",
    county: "",
    postalCode: "",
    country: "",
    profile_picture: "",
  });

  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [picError, setPicError] = useState("");

  // Load current user info
  useEffect(() => {

    // Check the token
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  console.log("Current auth token:", token);

    (async () => {
      try {
        const res = await getCurrentUser();
        const u = res.data;

        // --- Debug logs ---
        console.log("Logged-in user from backend:", u);
        console.log("User ID we are setting in formData:", u.id);

        setFormData({
          id: u.id,
          firstName: u.first_name || "",
          lastName: u.last_name || "",
          name: u.name || "",
          email: u.email || "",
          contact: u.contact_no || "",
          company: u.company || "",
          CardCode: u.cardcode || "",
          CardName: u.cardname || "",
          street: u.street || "",
          city: u.city || "",
          county: u.county || "",
          postalCode: u.postalCode || "",
          country: u.country || "",
          password: "",
          confirmPassword: "",
          profile_picture: u.profile_picture || "",
        });
        // ✅ Load existing profile picture if available
      if (u.profile_picture) 
      setPreview(toAbsoluteUrl(u.profile_picture));
      else setPreview(null);
      } catch (e) {
        console.error("Failed to load current user:", e);
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handlePicChange = async (e) => {
    setPicError("");
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Validate basic MIME first
      if (!ACCEPTED_TYPES.includes(file.type)) {
        if (REJECTED_TYPES.includes(file.type)) {
          throw new Error("HEIC/HEIF/AVIF is not supported. Please choose JPG/PNG/WebP.");
        }
        // Some devices report empty type; still try to process
      }

      const processed = await downscaleAndCompress(file);

      // Show preview (blob URL)
      const objectUrl = URL.createObjectURL(processed);
      setPreview(objectUrl);

      setFormData((p) => ({ ...p, _file: processed }));
    } catch (err) {
      console.error(err);
      setPicError(err.message || "Invalid or unsupported image file.");
      // revert input if failed
      e.target.value = "";
    }
  };

  // SAP Company Search
  const handleCompanySearch = async (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setLoadingSearch(true);
    try {
      const res = await getBusinessPartners(value);
      setCompanyResults(res.data || []);
    } catch (err) {
      console.error("Error fetching companies:", err);
      setCompanyResults([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSelectCompany = (company) => {
    setFormData((prev) => ({
      ...prev,
      CardCode: company.CardCode,
      CardName: company.CardName,
      company: company.CardName,
    }));
    setCompanyResults([]);
    setSearchTerm(company.CardName);
  };

  // Save profile
  const handleSubmit = async (e) => {
  e.preventDefault();
  setMessage("");
  setPicError("");

  if (formData.password && formData.password !== formData.confirmPassword) {
    toast.error("Passwords do not match!");
    return;
  }

  try {
    const payload = {
      first_name: formData.firstName || "",
      last_name: formData.lastName || "",
      name: formData.name || "",
      email: formData.email || "",
      contact_no: formData.contact || "",
      cardcode: formData.CardCode || "",
      cardname: formData.CardName || "",
      street: formData.street || "",
      city: formData.city || "",
      county: formData.county || "",
      postalCode: formData.postalCode || "",
      country: formData.country || "",
    };

    if (formData.password) payload.password = formData.password;
    if (formData._file) payload.profile_picture = formData._file;

    const resp = await updateUser(formData.id, payload);
    const u = resp.data.user;

    setFormData((p) => ({
        ...p,
        first_name: u.first_name ?? p.firstName,
        last_name: u.last_name ?? p.lastName,
        name: u.name ?? p.name,
        email: u.email ?? p.email,
        contact: u.contact_no ?? p.contact,
        CardCode: u.cardcode ?? p.CardCode,
        CardName: u.cardname ?? p.CardName,
        company: u.cardname ?? p.company,
        profile_picture: u.profile_picture ?? p.profile_picture,
        password: "",
        confirmPassword: "",
        _file: undefined,
      }));

    if (u.profile_picture) {
        setPreview(`${toAbsoluteUrl(u.profile_picture)}?t=${Date.now()}`);
      }

      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error.response || error);
      toast.error("Failed to update profile. Please try again.");
    }
};

// Decide which src to render
  const imgSrc =
    preview ||
    (formData.profile_picture
      ? `${toAbsoluteUrl(formData.profile_picture)}?t=${Date.now()}`
      : FALLBACK);

  return (
    <div className="py-4 px-10 lg:px-40 mt-1 md:px-16">
      <form className="space-y-10" onSubmit={handleSubmit}>
        
        {/* Personal Info */}
        <div>
          
          <h3 className="font-semibold text-gray-700 border-b border-gray-300 pb-2 mb-4">
            Personal Information
          </h3>
          <div className="grid md:grid-cols-3 grid-cols-1 gap-6 items-center">
            {/* Avatar */}
            <div className="relative flex flex-col justify-self-start md:order-last m-5 md:m-0">
              <div className="relative">
                <img
                  src={imgSrc}
                  alt="Profile"
                  onError={(e) => (e.currentTarget.src = FALLBACK)}
                  className="w-52 h-52 rounded-full object-cover border shadow-md bg-gray-100"
                />
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp" // narrow to reliable types
                  id="profilePicInput"
                  onChange={handlePicChange}
                  className="hidden"
                />
                <label
                  htmlFor="profilePicInput"
                  className="absolute bottom-2 right-2 flex items-center gap-1 bg-white/80 px-2 py-1 rounded-lg cursor-pointer transition hover:bg-white border"
                >
                  <Pencil size={14} />
                  <span className="text-xs">Edit</span>
                </label>
              </div>
              {picError && (
                <p className="mt-2 text-xs text-red-600 max-w-[18rem] text-center">
                  {picError}
                </p>
              )}
            </div>
            <div className="md:col-span-2 grid grid-cols-1 gap-2">
              <div className="flex flex-col">
                <label className="text-[80%] font-medium mb-1">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-3/4 px-2 py-1 border rounded text-[80%] focus:ring-1 focus:ring-blue-400 focus:outline-none"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-[80%] font-medium mb-1">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-3/4 px-2 py-1 border rounded text-[80%] focus:ring-1 focus:ring-blue-400 focus:outline-none"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-[80%] font-medium mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-3/4 px-2 py-1 border rounded text-[80%] focus:ring-1 focus:ring-blue-400 focus:outline-none"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-[80%] font-medium mb-1">Contact No</label>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  placeholder="Enter contact number"
                  className="w-2/4 px-2 py-1 border rounded text-[80%] focus:ring-1 focus:ring-blue-400 focus:outline-none"
                />
              </div>
            </div>

          </div>
        </div>

        {/* Company Info */}
        <div>
          <h3 className="font-semibold text-gray-700 border-b border-gray-300 pb-2 mb-4">
            Company Information
          </h3>
          <div className="grid grid-cols-1 gap-2">

            <div className="flex flex-col">
              <label className="text-[80%] font-medium mb-1">
                Company Name
              </label>
              <input
                type="text"
                name="company"
                value={formData.CardName || formData.company}
                readOnly
                className="w-3/4 px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Company Address */}
        <div>
          <h3 className="font-semibold text-gray-700 border-b border-gray-300 pb-2 mb-4">
            Company Address
          </h3>
          <div className="grid grid-cols-1 gap-2">
            <div className="flex flex-col">
              <label className="text-[80%] font-medium mb-1">Street Address</label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleChange}
                className="w-3/4 px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[80%] font-medium mb-1">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-2/4 px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[80%] font-medium mb-1">County</label>
              <input
                type="text"
                name="county"
                value={formData.county}
                onChange={handleChange}
                className="w-2/4 px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[80%] font-medium mb-1">Postal Code</label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                className="w-1/4 px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[80%] font-medium mb-1">Country</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-1/4 px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Security */}
        <div>
          <h3 className="font-semibold text-gray-700 border-b border-gray-300 pb-2 mb-4">
            Security
          </h3>
          <div className="grid grid-cols-1 gap-2">
            <div className="flex flex-col">
              <label className="text-[80%] font-medium mb-1">New Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter new password"
                className="w-2/4 px-2 py-1 border rounded text-[80%] focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[80%] font-medium mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                className="w-2/4 px-2 py-1 border rounded text-[80%] focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Save button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-blue-600 text-white text-sm py-2 px-4 rounded-lg hover:bg-blue-700 transition font-semibold"
          >
            Save
          </button>
        </div>
      </form>

      {message && <p className="mt-4 text-center text-sm text-gray-600">{message}</p>}
    </div>
  );
}

export default EditProfile;
